import React from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import axios from 'axios';
import type { Alert, Pool } from '../../types';
import AlertAckButtons from './AlertAckButtons';
import { getAlertStatusColor } from './alertStatus';
import { eventService } from '../../services/eventService';

type SnackbarState = {
  open: boolean;
  message: string;
  severity: 'success' | 'error';
};

type Props = {
  pool: Pool | null;
  alerts: Alert[];
  setAlerts: React.Dispatch<React.SetStateAction<Alert[]>>;
  setSnackbar: React.Dispatch<React.SetStateAction<SnackbarState>>;
};

/** PoolMonitoring 알림 탭 (P1-11 분할) */
const PoolMonitoringAlertsTab: React.FC<Props> = ({
  pool,
  alerts,
  setAlerts,
  setSnackbar,
}) => {
  const refreshAlerts = () => {
    if (!pool) return;
    axios
      .get(`/api/pools/${pool.id}/alerts`)
      .then((response) => {
        if (response.data.success && response.data.data) {
          setAlerts(response.data.data);
          setSnackbar({
            open: true,
            message: '알림 정보가 업데이트 완료',
            severity: 'success',
          });
        }
      })
      .catch((error) => {
        console.error(`Error fetching alerts for pool ${pool.id}:`, error);
        setSnackbar({
          open: true,
          message: '알림 정보 업데이트에 실패',
          severity: 'error',
        });
      });
  };

  return (
    <>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="h6">알림 목록</Typography>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={refreshAlerts}>
          새로고침
        </Button>
      </Box>

      {alerts.length > 0 ? (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>상태</TableCell>
                <TableCell>종류</TableCell>
                <TableCell>내용</TableCell>
                <TableCell>시간</TableCell>
                <TableCell width="120">작업</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {alerts.map((alert) => (
                <TableRow key={alert.id} hover>
                  <TableCell>
                    <Chip
                      label={
                        alert.status === 'active'
                          ? '진행중'
                          : alert.status === 'acknowledged'
                            ? '확인됨'
                            : '해결됨'
                      }
                      color={getAlertStatusColor(alert.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {alert.type === 'water_quality'
                      ? '수질'
                      : alert.type === 'safety'
                        ? '안전'
                        : alert.type === 'equipment'
                          ? '장비'
                          : '시스템'}
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {alert.title || alert.message}
                      </Typography>
                      {alert.title && alert.message && (
                        <Typography variant="body2" color="text.secondary">
                          {alert.message}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>{new Date(alert.created_at).toLocaleString()}</TableCell>
                  <TableCell>
                    <AlertAckButtons
                      eventId={eventService.resolveAckId(alert)}
                      disabled={alert.status === 'resolved'}
                      onDone={(kind) => {
                        setAlerts((prev) =>
                          prev.map((a) =>
                            a.id === alert.id
                              ? {
                                  ...a,
                                  status:
                                    kind === 'resolve' || kind === 'ack'
                                      ? ('resolved' as const)
                                      : ('acknowledged' as const),
                                }
                              : a
                          )
                        );
                        setSnackbar({
                          open: true,
                          message:
                            kind === 'false_positive'
                              ? '오탐으로 처리함'
                              : '알림을 확인함',
                          severity: 'success',
                        });
                      }}
                      onError={(msg) =>
                        setSnackbar({ open: true, message: msg, severity: 'error' })
                      }
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            알림 없음
          </Typography>
        </Paper>
      )}
    </>
  );
};

export default PoolMonitoringAlertsTab;
